import type { CopilotProvider, CopilotProviderId } from "../types";

/**
 * Provider genérico para APIs compatibles con OpenAI Chat Completions.
 * Soporta tool calling estándar (`tools` en request, `tool_calls` en respuesta).
 *
 * Servicios compatibles:
 *  - Groq           https://api.groq.com/openai/v1
 *  - OpenRouter     https://openrouter.ai/api/v1
 *  - Cerebras       https://api.cerebras.ai/v1
 *  - Together       https://api.together.xyz/v1
 *  - OpenAI         https://api.openai.com/v1
 */
function makeOpenAICompatible(
  id: CopilotProviderId,
  label: string,
  defaultBaseUrl: string,
  defaultModel: string,
  /** Endpoint para listar modelos. Algunos servicios no lo exponen sin auth. */
  listModelsPath: string | null = "/models",
): CopilotProvider {
  return {
    id,
    label,
    defaultBaseUrl,
    defaultModel,

    async listModels({ baseUrl, apiKey }) {
      if (!listModelsPath) return [];
      try {
        const res = await fetch(
          `${baseUrl.replace(/\/$/, "")}${listModelsPath}`,
          {
            headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
          },
        );
        if (!res.ok) return [];
        const json = (await res.json()) as { data?: { id: string }[] };
        return (json.data ?? []).map((m) => m.id).sort();
      } catch {
        return [];
      }
    },

    async chat({
      baseUrl,
      apiKey,
      model,
      systemPrompt,
      messages,
      tools,
      signal,
    }) {
      const url = `${baseUrl.replace(/\/$/, "")}/chat/completions`;
      const body: Record<string, unknown> = {
        model,
        temperature: 0.3,
        messages: [
          { role: "system", content: systemPrompt },
          ...messages.map((m) => {
            const base: Record<string, unknown> = {
              role: m.role,
              content: m.content,
            };
            if (m.tool_calls) {
              base.tool_calls = m.tool_calls.map((tc, i) => ({
                id: tc.id ?? `call_${i}`,
                type: "function",
                function: {
                  name: tc.function.name,
                  arguments:
                    typeof tc.function.arguments === "string"
                      ? tc.function.arguments
                      : JSON.stringify(tc.function.arguments),
                },
              }));
            }
            if (m.tool_call_id) base.tool_call_id = m.tool_call_id;
            if (m.name) base.name = m.name;
            return base;
          }),
        ],
      };
      if (tools && tools.length > 0) body.tools = tools;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `${label} (${res.status}): ${text || res.statusText || "fallo desconocido"}`,
        );
      }

      const json = (await res.json()) as {
        choices?: Array<{
          message?: {
            content?: string | null;
            tool_calls?: Array<{
              id?: string;
              function: { name: string; arguments: string };
            }>;
          };
        }>;
      };

      const msg = json.choices?.[0]?.message;
      const content = (msg?.content ?? "").trim();
      const rawCalls = msg?.tool_calls ?? [];

      const toolCalls = rawCalls.map((tc, idx) => {
        let args: Record<string, unknown> = {};
        try {
          args =
            typeof tc.function.arguments === "string"
              ? (JSON.parse(tc.function.arguments) as Record<string, unknown>)
              : (tc.function.arguments as Record<string, unknown>);
        } catch {
          args = {};
        }
        return {
          id: tc.id ?? `tc-${Date.now()}-${idx}`,
          name: tc.function.name,
          arguments: args,
        };
      });

      return { content, toolCalls };
    },
  };
}

export const groqProvider = makeOpenAICompatible(
  "groq",
  "Groq",
  "https://api.groq.com/openai/v1",
  "llama-3.3-70b-versatile",
);

export const openRouterProvider = makeOpenAICompatible(
  "openrouter",
  "OpenRouter",
  "https://openrouter.ai/api/v1",
  "meta-llama/llama-3.3-70b-instruct:free",
);

export const cerebrasProvider = makeOpenAICompatible(
  "cerebras",
  "Cerebras",
  "https://api.cerebras.ai/v1",
  "llama3.3-70b",
);
